/**
 * Maps a notification's `targetType` + `targetId` (set by the backend, see
 * `apps/common/notifications.py:TargetType`) to the page it should open.
 */

export interface NotificationTargetLike {
  targetType?: string;
  targetId?: string;
}

export interface NotificationRoute {
  path: string;
  state?: Record<string, unknown>;
}

export type NotificationAudience = "b2b" | "moderator";

function b2bRoute(type: string, id: string): NotificationRoute | null {
  switch (type) {
    case "matchRequest":
      return { path: "/b2b/requests" };
    case "deal":
      return { path: "/b2b/match-results" };
    // WP1.6: the matched supplier's view of a deal (accept / decline).
    case "opportunity":
      return id ? { path: "/b2b/opportunities", state: { openOpportunityId: id } } : { path: "/b2b/opportunities" };
    case "contract":
      return { path: "/b2b/contracts" };
    case "conversation":
      return id ? { path: "/b2b/messages", state: { openConversationId: id } } : { path: "/b2b/messages" };
    case "compliance":
      return { path: "/b2b/trust-compliance" };
    case "company":
      return { path: "/b2b/dashboard" };
    default:
      return null;
  }
}

function moderatorRoute(type: string, id: string): NotificationRoute | null {
  switch (type) {
    case "company":
      return { path: id ? `/moderator/companies/${id}` : "/moderator/companies" };
    // Contracts are 1:1 with deals and are edited on the deal detail page.
    case "deal":
    case "contract":
      return { path: id ? `/moderator/deals/${id}` : "/moderator/deals" };
    case "conversation":
      return { path: "/moderator/conversations" };
    default:
      return null;
  }
}

export function notificationRoute(
  notification: NotificationTargetLike,
  audience: NotificationAudience,
): NotificationRoute | null {
  const type = notification.targetType ?? "";
  const id = notification.targetId ?? "";
  if (!type) return null;
  return audience === "b2b" ? b2bRoute(type, id) : moderatorRoute(type, id);
}
